import React, { useState, useEffect } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

const CRM_PRESETS = {
  hubspot: { name: 'HubSpot', icon: 'fa-hubspot', color: '#FF7A59', default_events: ['lead_created', 'lead_qualified', 'lead_won', 'proposal_signed'] },
  salesforce: { name: 'Salesforce', icon: 'fa-cloud', color: '#00A1E0', default_events: ['lead_created', 'lead_updated', 'lead_won', 'lead_lost', 'proposal_signed'] },
  custom: { name: 'Custom Webhook', icon: 'fa-code', color: '#6366F1', default_events: [] },
};

const EVENT_LABELS = {
  lead_created: { label: 'Lead Created', icon: 'fa-user-plus', color: 'text-blue-500' },
  lead_updated: { label: 'Lead Updated', icon: 'fa-pen', color: 'text-gray-500' },
  lead_qualified: { label: 'Lead Qualified', icon: 'fa-star', color: 'text-yellow-500' },
  lead_won: { label: 'Lead Won', icon: 'fa-trophy', color: 'text-green-500' },
  lead_lost: { label: 'Lead Lost', icon: 'fa-xmark', color: 'text-red-500' },
  proposal_created: { label: 'Proposal Created', icon: 'fa-file-invoice', color: 'text-purple-500' },
  proposal_signed: { label: 'Proposal Signed', icon: 'fa-signature', color: 'text-green-600' },
  proposal_declined: { label: 'Proposal Declined', icon: 'fa-ban', color: 'text-red-500' },
  mining_completed: { label: 'Mining Completed', icon: 'fa-satellite-dish', color: 'text-sky-500' },
  enrichment_completed: { label: 'Enrichment Done', icon: 'fa-brain', color: 'text-purple-500' },
};

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [testing, setTesting] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', events: [], crm_type: 'custom', headers: {}, is_active: true });
  const [editId, setEditId] = useState(null);

  // ESC key to close modals
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setShowCreate(false); setEditId(null); } };
    if (showCreate) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showCreate]);

  const fetch = async () => {
    try {
      const res = await api.get('/api/webhooks');
      setWebhooks(res.data.webhooks || []);
      setEvents(res.data.available_events || []);
      setLoading(false);
    } catch { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const selectPreset = (key) => {
    const p = CRM_PRESETS[key];
    setForm({ ...form, crm_type: key, name: p.name, events: [...p.default_events] });
  };

  const toggleEvent = (ev) => {
    setForm(prev => ({
      ...prev,
      events: prev.events.includes(ev) ? prev.events.filter(e => e !== ev) : [...prev.events, ev]
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.url) { toast.error('URL is required'); return; }
    try {
      if (editId) {
        await api.put(`/api/webhooks/${editId}`, form);
        toast.success('Webhook updated');
      } else {
        await api.post('/api/webhooks', form);
        toast.success('Webhook created');
      }
      setShowCreate(false);
      setEditId(null);
      setForm({ name: '', url: '', events: [], crm_type: 'custom', headers: {}, is_active: true });
      fetch();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/api/webhooks/${id}`); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await api.post(`/api/webhooks/${id}/test`);
      if (res.data.success) toast.success(`Test successful (${res.data.status_code})`);
      else toast.error(`Test failed (${res.data.status_code || 'timeout'})`);
      fetch();
    } catch { toast.error('Test failed'); }
    setTesting(null);
  };

  const startEdit = (wh) => {
    setForm({ name: wh.name, url: wh.url, events: wh.events, crm_type: wh.crm_type, headers: wh.headers || {}, is_active: wh.is_active });
    setEditId(wh.id);
    setShowCreate(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="webhooks-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">CRM Integrations</h1>
          <p className="text-text-muted text-sm mt-1">Connect to HubSpot, Salesforce, or custom webhooks</p>
        </div>
        <button onClick={() => { setShowCreate(true); setEditId(null); setForm({ name: '', url: '', events: [], crm_type: 'custom', headers: {}, is_active: true }); }} className="btn-primary text-xs py-2.5" data-testid="add-webhook-btn"><i className="fa-solid fa-plus mr-1.5" />Add Integration</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card-interactive p-4">
          <div className="flex items-center gap-2 mb-2"><i className="fa-solid fa-link text-primary text-xs" /><span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Total Integrations</span></div>
          <p className="font-heading text-2xl font-bold text-text-primary">{webhooks.length}</p>
        </div>
        <div className="card-interactive p-4">
          <div className="flex items-center gap-2 mb-2"><i className="fa-solid fa-check-circle text-primary text-xs" /><span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Active</span></div>
          <p className="font-heading text-2xl font-bold text-primary">{webhooks.filter(w => w.is_active).length}</p>
        </div>
        <div className="card-interactive p-4">
          <div className="flex items-center gap-2 mb-2"><i className="fa-solid fa-paper-plane text-accent text-xs" /><span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Total Triggers</span></div>
          <p className="font-heading text-2xl font-bold text-text-primary">{webhooks.reduce((a, w) => a + (w.trigger_count || 0), 0)}</p>
        </div>
      </div>

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <div className="card-interactive py-20 text-center">
          <i className="fa-solid fa-plug text-4xl text-text-muted opacity-20 mb-4 block" />
          <p className="text-text-muted font-medium">No integrations configured</p>
          <p className="text-text-muted text-xs mt-1">Connect your CRM to sync lead data automatically</p>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((wh, i) => {
            const preset = CRM_PRESETS[wh.crm_type] || CRM_PRESETS.custom;
            return (
              <div key={wh.id} className="card-interactive p-5 animate-slide-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`webhook-${wh.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: preset.color + '15' }}>
                      <i className={`fa-brands ${preset.icon} text-lg`} style={{ color: preset.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-text-primary">{wh.name}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${wh.is_active ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${wh.is_active ? 'bg-primary' : 'bg-gray-400'}`} />
                          {wh.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-xs text-text-muted font-mono truncate max-w-xs">{wh.url}</p>
                      <div className="flex gap-1 mt-1.5">
                        {(wh.events || []).slice(0, 4).map(ev => (
                          <span key={ev} className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-surface-alt text-text-muted">{ev.replace(/_/g, ' ')}</span>
                        ))}
                        {(wh.events || []).length > 4 && <span className="text-[9px] text-text-muted">+{wh.events.length - 4}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="hidden sm:block text-right">
                      <p className="text-xs text-text-muted">{wh.trigger_count || 0} triggers</p>
                      {wh.last_status && <p className={`text-[10px] font-mono ${wh.last_status >= 200 && wh.last_status < 300 ? 'text-primary' : 'text-destructive'}`}>Last: {wh.last_status}</p>}
                    </div>
                    <button onClick={() => handleTest(wh.id)} disabled={testing === wh.id} className="btn-secondary text-xs py-1.5 px-3" data-testid={`test-webhook-${wh.id}`}>
                      {testing === wh.id ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-play mr-1" />Test</>}
                    </button>
                    <button onClick={() => startEdit(wh)} className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-alt transition-all"><i className="fa-solid fa-pen text-xs" /></button>
                    <button onClick={() => handleDelete(wh.id)} className="p-1.5 rounded-lg text-text-muted hover:text-destructive hover:bg-destructive-light transition-all"><i className="fa-solid fa-trash text-xs" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setShowCreate(false); setEditId(null); }}>
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-xl max-h-[90vh] overflow-y-auto animate-slide-up relative" onClick={e => e.stopPropagation()} data-testid="webhook-modal">
            <button onClick={() => { setShowCreate(false); setEditId(null); }} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-surface-alt transition-colors z-10" data-testid="close-webhook-modal"><i className="fa-solid fa-xmark text-text-muted" /></button>
            <h2 className="font-heading text-xl font-bold text-text-primary mb-5">{editId ? 'Edit Integration' : 'New Integration'}</h2>

            {/* Presets */}
            {!editId && (
              <div className="grid grid-cols-3 gap-3 mb-5">
                {Object.entries(CRM_PRESETS).map(([key, p]) => (
                  <button key={key} onClick={() => selectPreset(key)} className={`p-4 rounded-xl border-2 transition-all text-center ${form.crm_type === key ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/20'}`} data-testid={`preset-${key}`}>
                    <i className={`fa-brands ${p.icon} text-xl mb-1.5`} style={{ color: p.color }} />
                    <p className="text-xs font-semibold text-text-primary">{p.name}</p>
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Integration Name</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input-field h-9 text-sm" placeholder="My HubSpot" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Webhook URL *</label><input value={form.url} onChange={e => setForm({...form, url: e.target.value})} className="input-field h-9 text-sm font-mono" placeholder="https://hooks.hubspot.com/..." data-testid="webhook-url-input" /></div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Events to Trigger</label>
                <div className="grid grid-cols-2 gap-2">
                  {events.map(ev => {
                    const evInfo = EVENT_LABELS[ev] || { label: ev, icon: 'fa-bell', color: 'text-gray-500' };
                    const selected = form.events.includes(ev);
                    return (
                      <button key={ev} type="button" onClick={() => toggleEvent(ev)} className={`flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all ${selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/20'}`} data-testid={`event-${ev}`}>
                        <i className={`fa-solid ${selected ? 'fa-check-square text-primary' : 'fa-square text-gray-300'} text-xs`} />
                        <i className={`fa-solid ${evInfo.icon} ${evInfo.color} text-xs`} />
                        <span className="text-xs font-medium text-text-primary">{evInfo.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-alt">
                <span className="text-xs font-medium text-text-primary">Active</span>
                <button type="button" onClick={() => setForm({...form, is_active: !form.is_active})} className={`w-9 h-5 rounded-full transition-all ${form.is_active ? 'bg-primary' : 'bg-gray-200'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${form.is_active ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
                </button>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setEditId(null); }} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" className="btn-primary text-sm" data-testid="save-webhook-btn"><i className="fa-solid fa-save mr-1.5" />{editId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
